/** Standalone toolbar wrapper around TimeWindowPanel: a trigger button showing the
 *  current window ("⟳ Last 15 minutes", "21 Oct 14:00 → 15:00 UTC"), opening a Popover
 *  with the panel and a Clear action. Pages keep their own wire formatting
 *  (boundToIso for URL params). */
import DateRangeIcon from '@mui/icons-material/DateRange';
import UpdateIcon from '@mui/icons-material/Update';
import { Box, Button, Popover } from '@mui/material';
import { useRef, useState } from 'react';

import { capitalise, isLiveWindow, windowLabel } from './labels';
import { TimeWindowPanel } from './TimeWindowPanel';
import type { TimeWindowPickerProps, TimeWindowTz, TimeWindowValue } from './types';

export function TimeWindowPicker(props: TimeWindowPickerProps) {
  const {
    label = 'Time range',
    defaultTz = 'utc',
    allowOpenEnded = false,
    allowFuture = false,
    disabled,
    onTzChange,
  } = props;
  // The discriminated union keeps consumers honest; inside, both arms are a window.
  const value: TimeWindowValue = props.value;
  const keepRelative = props.keepRelative !== false;
  const onChange = props.onChange as (v: TimeWindowValue) => void;

  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  // The tz the trigger label renders in — follows the panel's toggle so the
  // button shows the range the way the user last looked at it.
  const [tz, setTz] = useState<TimeWindowTz>(defaultTz);

  const [from, to] = value;
  const text = windowLabel(from, to, { tz, zone: true });
  const live = isLiveWindow(from, to);

  return (
    <>
      <Button
        ref={anchorRef}
        variant="outlined"
        size="small"
        color="inherit"
        disabled={disabled}
        startIcon={live ? <UpdateIcon fontSize="small" /> : <DateRangeIcon fontSize="small" />}
        onClick={() => setOpen(true)}
        aria-label={text ? `${label}: ${text}${live ? ', live' : ''}` : label}
        sx={{
          // Blend in with the size="small" outlined inputs the trigger sits
          // next to in filter toolbars: same 40px height, same border tone.
          height: 40,
          px: 1.5,
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
          color: text ? 'text.primary' : 'text.secondary',
          borderColor: 'rgba(0, 0, 0, 0.23)',
          '& .MuiButton-startIcon': { color: live ? 'primary.main' : 'inherit' },
          '&:hover': { borderColor: 'text.primary', backgroundColor: 'transparent' },
        }}
      >
        {text ? capitalise(text) : label}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5 } } }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 1.5 }}>
          <TimeWindowPanel
            initialFrom={from}
            initialTo={to}
            defaultTz={defaultTz}
            requireBothBounds={!allowOpenEnded}
            allowFuture={allowFuture}
            keepRelative={keepRelative}
            tz={tz}
            onTzChange={(next) => {
              setTz(next);
              onTzChange?.(next);
            }}
            onClear={
              text
                ? () => {
                    onChange([null, null]);
                    setOpen(false);
                  }
                : undefined
            }
            onApply={(range) => {
              onChange(range);
              setOpen(false);
            }}
          />
        </Box>
      </Popover>
    </>
  );
}
